import * as React from 'react';
import { Async } from '@uifabric/utilities/lib/Async';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCacheDispatch, IItemCacheThunkAction } from '../../../store/actions/ItemCacheActions';
import { blockProgressExpiration } from '../../../operations/progress';
import { IAsyncActionCreator } from '../../../store/actions/createActionCreator';
import { ProgressPhase } from '../../../facets/ProgressFacets';
import { Fragment } from '../../../utilities/components/Fragment';

/**
 * @internal
 * @deprecated This component is no longer used.
 */
export interface IExpirationProps {
    itemKey: string | undefined;
    progressPhase: ProgressPhase | undefined;
    isBlocked: boolean;
    onExpire: (payload: { itemKey: string; }) => void;
}

/**
 * Component which indicates that a progress item should be expired
 * if it is in a completed state for a minimum amount of time.
 * @internal
 * @deprecated This component is no longer used.
 */
export class ProgressExpiration extends React.PureComponent<IExpirationProps> {
    private _async: Async;
    /**
     * Id for a pending timeout, after which the progress item will be treated as expired.
     * If anything changes in the meantime, this timeout should get reset.
     */
    private _timeoutId: number | undefined;

    constructor(props: IExpirationProps) {
        super(props);

        this._async = new Async(this);
    }

    public componentDidMount(): void {
        this._expire(this.props);
    }

    public componentDidUpdate(previousProps: IExpirationProps): void {
        if (this.props.itemKey !== previousProps.itemKey ||
            this.props.progressPhase !== previousProps.progressPhase ||
            this.props.isBlocked !== previousProps.isBlocked) {
            // If the target item has changed or its phase has changed,
            // reset the expiration timeout.
            if (this._timeoutId) {
                this._async.clearTimeout(this._timeoutId);
                this._timeoutId = undefined;
            }

            this._expire(this.props);
        }
    }

    public componentWillUnmount(): void {
        this._async.dispose();
    }

    public render(): JSX.Element {
        return (
            <Fragment>{ this.props.children }</Fragment>
        );
    }

    private _expire(props: IExpirationProps): void {
        const {
            onExpire,
            progressPhase,
            itemKey
        } = props;

        if (!props.isBlocked && itemKey && (
            progressPhase === ProgressPhase.completed ||
            progressPhase === ProgressPhase.canceled)) {
            this._timeoutId = this._async.setTimeout(() => {
                onExpire({
                    itemKey: itemKey
                });
            }, 5000);
        }
    }
}

/**
 * @internal
 * @deprecated This component is no longer used.
 */
export interface IBlockProgressExpirationProps {
    itemCacheDispatch: IItemCacheDispatch;
}

/**
 * Component which, while mounted, prevents progress items from being expired automatically.
 * @internal
 * @deprecated This component is no longer used.
 */
export const BlockProgressExpiration: React.ComponentType<{}> = connectToItemCache()(
    class extends React.Component<IBlockProgressExpirationProps> {
        private _unblockProgressExpiration: IAsyncActionCreator<{}, IItemCacheThunkAction<void>> | undefined;

        public componentDidMount(): void {
            this._unblockProgressExpiration = this.props.itemCacheDispatch(blockProgressExpiration({}));
        }

        public componentWillUnmount(): void {
            const unblockProgressExpiration = this._unblockProgressExpiration;

            if (unblockProgressExpiration) {
                this.props.itemCacheDispatch(unblockProgressExpiration({}));
            }
        }

        public render(): JSX.Element {
            return (
                <Fragment>{ this.props.children }</Fragment>
            );
        }
    }
);
